import React from "react";
import { Link } from "react-router-dom";
import useAuthContext from "../../providers/authProvider";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts";
import { updateTeamGroup } from "../../services/teams";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Select from "react-select";
import { selectNumberStyles } from "../../components/shared/styles";

const ClubProfile = ({ groups }) => {
  const { club } = useAuthContext();

  const onDragEnd = async ({ source, destination }) => {
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

    const filteredGroups = groups.filter(group => group.id); 
    const getPosition = index => Number(filteredGroups[index]?.position) || 0;

    let newPosition;
    if (destination.index === 0) {
      newPosition = getPosition(destination.index) / 2;
    } else if (destination.index === filteredGroups.length - 1) {
      newPosition = getPosition(filteredGroups.length - 1) + 1;
    } else {
      newPosition = (getPosition(destination.index - 1) + getPosition(destination.index)) / 2;
    }

    const [removed] = groups.splice(source.index, 1);
    groups.splice(destination.index, 0, removed);

    try {
      await updateTeamGroup(removed.id, { name: removed.name, position: newPosition.toFixed(3) });
    } catch (error) {
      console.error("Error updating group positions:", error);
    }
  };

  return (
    <div className="club-home-details">
      <div className="club-home-details-image">
        <img src={club?.logo} alt="Club Logo" />
      </div>
      <div className="club-home-details-description">
        <h2>{club?.name}</h2>
        <hr />

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="group">
            {provided => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="club-home-details-description-teams">
                {groups.filter(g => g.id).map((group, index) => (
                  <TeamGroup key={group.id} group={group} index={index} draggable />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="club-home-details-description-teams">
          {groups.filter((g) => !g.id).map((group, index) => (
            <TeamGroup key={index} group={group} index={index} />
          ))}
        </div>

        <div className="club-home-details-description-text">
          {club?.description}
        </div>
      </div>
    </div>
  );
};

export default ClubProfile;

const TeamGroup = ({ group, index, draggable = false }) => {
  const { id, name, teams } = group;
  const [visibleGroupCounts, setVisibleGroupCounts] = useLocalStorage("visibleGroupCounts", {});
  const visibleTeams = visibleGroupCounts[id] || 10;
  const { t } = useTranslation();

  const options = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 75, label: "75" },
    { value: 1000, label: t("common.all") },
  ];

  const content = (dragProps = {}) => (
    <div className="club-block" ref={dragProps.innerRef} {...dragProps.draggableProps}>
      <div className="club-home-details-description-teams-header">
        <div className="team-group-name">
          {draggable && <i {...dragProps.dragHandleProps} className="icon-profile icon-options-vertical"/>}
          <h4>{name || t("common.teams")}</h4>
        </div>
        <div className="team-group-controls">
          <Select
            value={options.find(option => option.value === visibleTeams)}
            onChange={selectedOption => setVisibleGroupCounts(prev => ({ ...prev, [id]: Number(selectedOption.value) }))}
            options={options}
            isSearchable={false}
            styles={selectNumberStyles}
          />
          <span className="team-total">{t("common.of")} {teams.length}</span>
        </div>
      </div>
      <div className="teams-link">
        {teams.sort((a, b) => a.position - b.position).slice(0, visibleTeams).map(({ name, id }) => (
          <Link key={id} to={`/club_admin/teams/${id}`}>{name}</Link>
        ))}
      </div>
    </div>
  );

  if (draggable && id) {
    return (
      <Draggable draggableId={id.toString()} index={index}>
        {(provided) =>
          content({
            innerRef: provided.innerRef,
            draggableProps: provided.draggableProps,
            dragHandleProps: provided.dragHandleProps,
          })
        }
      </Draggable>
    );
  }

  return content();
};



